import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Alert,
  Chip,
  Stack,
  FormHelperText
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { styled } from '@mui/material/styles';

// Import existing utilities
import { validateRequired } from '../utils/formValidation';
import { logError, logWarning, logInfo } from '../utils/logger';

const StyledButton = styled(Button)(({ theme, selected }) => ({
  padding: theme.spacing(2),
  textTransform: 'capitalize',
  borderRadius: theme.spacing(1),
  border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: selected ? theme.palette.primary.main : 'transparent',
  color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: selected 
      ? theme.palette.primary.dark 
      : theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  },
  '&:focus': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '2px',
  },
  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
}));

const CategoryOptions = [
  {
    key: 'content_creator',
    label: 'Content Creator',
    icon: PersonIcon,
    description: 'For individual creators and content producers'
  },
  {
    key: 'small_business',
    label: 'Small Business',
    icon: BusinessIcon,
    description: 'For small businesses and entrepreneurs'
  },
  {
    key: 'agency',
    label: 'Marketing Agency',
    icon: BusinessIcon,
    description: 'For marketing agencies and service providers'
  },
  {
    key: 'events',
    label: 'Event Organizer',
    icon: EventIcon,
    description: 'For event organizers and promoters'
  },
  {
    key: 'nonprofit',
    label: 'Non-profit',
    icon: HelpOutlineIcon,
    description: 'For non-profit organizations and charities'
  },
  {
    key: 'freelancer',
    label: 'Freelancer',
    icon: PersonIcon,
    description: 'For freelancers and independent professionals'
  },
  {
    key: 'consultant',
    label: 'Consultant',
    icon: PersonIcon,
    description: 'For consultants and advisory services'
  },
  {
    key: 'ecommerce',
    label: 'E-commerce',
    icon: BusinessIcon,
    description: 'For online stores and e-commerce businesses'
  },
  {
    key: 'influencer',
    label: 'Influencer',
    icon: PersonIcon,
    description: 'For social media influencers and personal brands'
  },
  {
    key: 'other',
    label: 'Other',
    icon: HelpOutlineIcon,
    description: 'Specify your custom use case'
  }
];

/**
 * Validates the other field input
 * @param {string} value - Other field value
 * @returns {string|null} Error message or null if valid
 */
const validateOtherField = (value) => {
  if (!value || typeof value !== 'string' || value.trim().length === 0) {
    return 'Please specify your other use case.';
  }

  const trimmedValue = value.trim();

  if (trimmedValue.length < 3) {
    return 'Please provide at least 3 characters.';
  }

  if (trimmedValue.length > 200) {
    logWarning('User attempted extremely long other field input', { 
      length: trimmedValue.length 
    });
    return 'Please keep it under 200 characters.';
  }

  // Check for suspicious patterns
  if (/^\d+$/.test(trimmedValue)) {
    return 'Please provide a meaningful description.';
  }

  // Check for excessive repeating characters
  if (/(.)\1{9,}/.test(trimmedValue)) {
    return 'Please provide a valid description.';
  }

  return null;
};

export default function OnboardingStep2({
  setFormData,
  formData = {},
  onValidationChange,
  isSubmitting = false
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Initialize state from formData prop
  const [selectedCategories, setSelectedCategories] = useState(
    formData.account_categories || []
  );
  const [otherDetails, setOtherDetails] = useState(
    formData.other_details || ''
  );
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false); // Set to true to show initial validation errors

  // Validation function using existing utilities
  const validateForm = useCallback(() => {
    const newErrors = {};

    try {
      // Category selection validation
      if (!selectedCategories || selectedCategories.length === 0) {
        newErrors.categories = 'Please select at least one account category.';
      }

      // Other field validation if other category is selected
      if (selectedCategories.includes('other')) {
        const otherError = validateOtherField(otherDetails);
        if (otherError) {
          newErrors.other = otherError;
        }
      }

      const isValid = Object.keys(newErrors).length === 0;
      setErrors(newErrors);

      // Log validation results for debugging
      logInfo('OnboardingStep2 validation completed', {
        isValid,
        selectedCategories: selectedCategories.length,
        hasOtherDetails: selectedCategories.includes('other') ? !!otherDetails : null,
        errorCount: Object.keys(newErrors).length
      });

      return { isValid, errors: newErrors };

    } catch (error) {
      logError('OnboardingStep2 validation error', {
        error: error.message,
        selectedCategories,
        otherDetails: otherDetails ? otherDetails.length : 0
      });
      
      return { 
        isValid: false, 
        errors: { general: 'Validation error occurred. Please try again.' }
      };
    }
  }, [selectedCategories, otherDetails]);

  // Handle category selection
  const handleCategoryToggle = useCallback((categoryKey) => {
    if (isSubmitting) return;

    try {
      setTouched(true);
      setSelectedCategories(prev => {
        const isCurrentlySelected = prev.includes(categoryKey);
        let newCategories;

        if (isCurrentlySelected) {
          newCategories = prev.filter(cat => cat !== categoryKey);
          logInfo('OnboardingStep2 category deselected', { category: categoryKey });
        } else {
          newCategories = [...prev, categoryKey];
          logInfo('OnboardingStep2 category selected', { category: categoryKey });
        }

        return newCategories;
      });

      // Clear other details if other category is deselected
      if (categoryKey === 'other' && selectedCategories.includes('other')) {
        setOtherDetails('');
        logInfo('OnboardingStep2 other details cleared');
      }

    } catch (error) {
      logError('OnboardingStep2 category toggle error', {
        error: error.message,
        categoryKey,
        isSubmitting
      });
    }
  }, [isSubmitting, selectedCategories]);

  // Handle other field input
  const handleOtherChange = useCallback((event) => {
    try {
      const value = event.target.value;
      setOtherDetails(value);
      setTouched(true);

      // Clear field-specific errors when user starts typing valid content
      if (errors.other && value.trim().length >= 3) {
        setErrors(prev => {
          const { other, ...rest } = prev;
          return rest;
        });
      }

    } catch (error) {
      logError('OnboardingStep2 other field change error', {
        error: error.message
      });
    }
  }, [errors.other]);

  // Handle keyboard events for accessibility
  const handleKeyDown = useCallback((event, categoryKey) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCategoryToggle(categoryKey);
    }
  }, [handleCategoryToggle]);

  // Update form data and validation when state changes
  useEffect(() => {
    try {
      const { isValid, errors: validationErrors } = validateForm();
      
      const newFormData = {
        account_categories: selectedCategories,
        other_details: otherDetails,
        isValid
      };

      // Update parent form data
      if (setFormData) {
        setFormData(newFormData, 2);
      }

      // Call validation callback
      if (onValidationChange) {
        onValidationChange(isValid, validationErrors);
      }

    } catch (error) {
      logError('OnboardingStep2 form data update error', {
        error: error.message,
        selectedCategories,
        otherDetails: otherDetails ? otherDetails.length : 0
      });
    }
  }, [selectedCategories, otherDetails, validateForm, setFormData, onValidationChange]);

  return (
    <Box
      component="form"
      aria-label="Account category selection"
      noValidate
      sx={{ width: '100%' }}
    >
      {/* Header */}
      <Typography 
        variant="h5" 
        component="h2" 
        gutterBottom
        sx={{ 
          mb: 3,
          textAlign: 'center',
          fontWeight: 600
        }}
      >
        Tell us about yourself
      </Typography>

      <Typography 
        variant="body1" 
        color="text.secondary"
        sx={{ 
          mb: 4,
          textAlign: 'center',
          maxWidth: 600,
          mx: 'auto'
        }}
      >
        Select the categories that best describe how you'll use our platform. 
        You can choose multiple options.
      </Typography>

      {/* Category Selection */}
      <Box
        role="group"
        aria-label="Account category options"
        sx={{ mb: 3 }}
      >
        <Box
          display="grid"
          gridTemplateColumns={isMobile ? '1fr' : 'repeat(2, 1fr)'}
          gap={2}
          sx={{ mb: 2 }}
        >
          {CategoryOptions.map((option) => {
            const IconComponent = option.icon;
            const isSelected = selectedCategories.includes(option.key);
            
            return (
              <StyledButton
                key={option.key}
                selected={isSelected}
                onClick={() => handleCategoryToggle(option.key)}
                onKeyDown={(e) => handleKeyDown(e, option.key)}
                disabled={isSubmitting}
                startIcon={<IconComponent />}
                aria-label={`Select ${option.label.toLowerCase()} category`}
                aria-pressed={isSelected}
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  height: 'auto',
                  py: 2,
                  px: 3,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" component="div">
                    {option.label}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    component="div"
                  >
                    {option.description}
                  </Typography>
                </Box>
              </StyledButton>
            );
          })}
        </Box>

        {/* Error message for category selection */}
        {touched && errors.categories && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors.categories}
          </Alert>
        )}
      </Box>

      {/* Other field */}
      {selectedCategories.includes('other') && (
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Specify other use case"
            variant="outlined"
            value={otherDetails}
            onChange={handleOtherChange}
            disabled={isSubmitting}
            error={!!errors.other}
            helperText={
              errors.other || 
              `${otherDetails.length}/200 characters`
            }
            multiline
            rows={3}
            inputProps={{
              maxLength: 200,
              'aria-describedby': 'other-helper-text',
              'aria-invalid': !!errors.other
            }}
            sx={{ mt: 2 }}
          />
        </Box>
      )}

      {/* Selected Categories Summary */}
      {selectedCategories.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Categories:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {selectedCategories.map((categoryKey) => {
              const option = CategoryOptions.find(opt => opt.key === categoryKey);
              return (
                <Chip
                  key={categoryKey}
                  label={option?.label || categoryKey}
                  color="primary"
                  variant="filled"
                  size="small"
                  onDelete={!isSubmitting ? () => handleCategoryToggle(categoryKey) : undefined}
                  disabled={isSubmitting}
                />
              );
            })}
          </Stack>
        </Box>
      )}

    </Box>
  );
}
